/**
 * MESSAGE ACTIVITY BODY — corpo dell'accordion di un Message importato, embed
 * nell'AccordionDetails di ActivityTimeline (sezione Email).
 *
 * Struttura allineata a {@link EmailEditFormBody} (direzione, data + Commenti/
 * Associazioni via {@link ActivityTools}), con UNA differenza: i campi del messaggio
 * sono READ-ONLY (i Message sono email importate dalla casella, non attività loggate
 * a mano). Commenti e Associazioni restano interattivi come per le altre attività.
 *
 * Il pezzo centrale è il CORPO della mail: `bodyHtml` reso in un iframe sandboxed
 * (contenuto non fidato — stessa scelta della smart inbox), con fallback al testo
 * semplice `body` per le mail senza parte HTML. L'oggetto non si ripete qui: è già il
 * titolo della riga dell'accordion.
 *
 * Il link alla smart inbox (/inbox?messageId=X) sta PRIMA del corpo, perché il corpo può
 * essere lungo: là si trova il dettaglio completo — info estratte, allegati, AI — che qui
 * non replichiamo.
 *
 * Il `message` che arriva dalla prop è la proiezione LEGGERA (metadati, corpo vuoto) ricostruita
 * dal bulk: serve a montare l'accordion subito. Il CORPO arriva con la fetch on-expand
 * (GET /message/{id}/summary, dispatch in useEntityActivities) e si legge da
 * `state.messages.summariesById`: finché non è in cache, l'area del corpo mostra un caricamento,
 * mentre metadati, link e tool sono già lì.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import LaunchIcon from '@mui/icons-material/Launch';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import { formatDateTime, formatFileSize } from '@crm/shared';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAppSelector } from '../../store/hooks';
import { useIsCoreItemEnabled } from '../../plugin/useCoreItems';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { ActivityTools } from '../../components/ActivityTools';
import { SandboxedHtmlFrame } from '../../components/SandboxedHtmlFrame';
import { useAttachmentDownload } from './useAttachmentDownload';
import type { MessageSummary } from './messageTypes';

interface MessageActivityBodyProps {
  message: MessageSummary;
}

export function MessageActivityBody({ message }: MessageActivityBodyProps) {
  const { t } = useTranslation();
  // Inbox spenta dalla pagina «Voci del CRM» (FILO-53): la mail resta, il link no.
  const inboxEnabled = useIsCoreItemEnabled('inbox');
  const downloadAttachment = useAttachmentDownload();

  const messageFqn = getEntityFqn('Message');
  const inbound = message.direction === 'IN';

  // Proiezione COL CORPO: presente solo dopo la fetch on-expand. `undefined` = corpo in arrivo
  // (non "mail vuota"): distinguere i due casi da un body === '' sarebbe una congettura.
  const loaded = useAppSelector((state) => state.messages.summariesById[message.messageId]);

  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'edit',
    entityFqn: messageFqn ?? '',
    entityId: message.messageId,
  });

  return (
    <div className="message-activity-body">
      <div className="task-edit-body__grid task-edit-body__grid--2col">
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('message.activity.direction')}</div>
          <div>{inbound ? t('message.activity.received') : t('message.activity.sent')}</div>
        </div>
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('message.activity.date')}</div>
          <div>{formatDateTime(message.receiptDate)}</div>
        </div>
      </div>

      {/* Link alla smart inbox PRIMA del corpo: il corpo di una mail può essere lungo (e vive in un
          iframe ad altezza fissa con scroll interno), quindi un link sotto sarebbe fuori vista. */}
      {inboxEnabled && (
        <Link to={`/inbox?messageId=${message.messageId}`} className="message-activity-body__inbox">
          <LaunchIcon fontSize="small" />
          {t('message.activity.openInInbox')}
        </Link>
      )}

      {/* Corpo della mail. L'oggetto NON si ripete: è già il titolo della riga dell'accordion. */}
      {!loaded ? (
        <div className="message-activity-body__loading">
          <CircularProgress size={20} />
        </div>
      ) : loaded.bodyHtml ? (
        // HTML di una mail = contenuto non fidato: iframe isolato, come fa la smart inbox.
        // Mai nel DOM dell'app. Il contenitore vive in SandboxedHtmlFrame.
        <SandboxedHtmlFrame
          className="message-activity-body__html"
          title={t('message.bodyHtmlTitle')}
          html={loaded.bodyHtml}
        />
      ) : (
        <div className="message-activity-body__text text-sm">{loaded.body}</div>
      )}

      {/* Allegati (TS-108) sotto il corpo, come nella smart inbox (stessa classe `.att`).
          Vengono dalla prop e NON da `loaded`: la proiezione bulk li porta gia', quindi
          compaiono anche mentre il corpo sta ancora arrivando. Dentro l'iframe non si
          potrebbero mettere — la' vive l'HTML della mail, e la sandbox lo tiene separato. */}
      {message.attachments && message.attachments.length > 0 && (
        <div className="att">
          {message.attachments.map((att) => (
            <button
              key={att.messageAttachmentId}
              type="button"
              title={t('message.attachmentDownload')}
              onClick={() => downloadAttachment(att)}
            >
              <AttachFileIcon fontSize="inherit" />
              <span>{att.fileName}</span>
              <span className="sz">{formatFileSize(att.size)}</span>
              <DownloadIcon fontSize="inherit" />
            </button>
          ))}
        </div>
      )}

      {/* Commenti + Associazioni: interattivi come per le altre attività (il vincolo
          read-only vale sui campi del messaggio, non su queste meta-interazioni). */}
      <ActivityTools
        {...recordsPickerProps}
        records={currentlyLinked}
        onChange={(val) => handleRecordsChange?.(val)}
        recordsSaving={recordsSaving}
        entityFqn={messageFqn ?? ''}
        entityId={message.messageId}
      />
    </div>
  );
}
